import { useState, useEffect, useRef } from "react";
import { Quote, ChevronLeft, ChevronRight } from "lucide-react";

const TESTIMONIALS = [
  {
    name: "Suresh Naidu",
    role: "Founder · Hyderabad",
    quote:
      "We chose GenieX after looking at every premium brand in the country. The level of personalisation, the calmness of the installation, and the way our home now anticipates us — it's been a quiet revelation.",
  },
  {
    name: "Vignesh Reddy",
    role: "Villa Owner · Jubilee Hills",
    quote:
      "From the moment our advisor walked in, we knew this was different. The Dynamic Series replaced thirty switches in our home — and somehow the rooms feel more spacious now. Genuinely architectural.",
  },
  {
    name: "Alekhya Subramanyam",
    role: "Interior Architect · Vijayawada",
    quote:
      "I specify GenieX on every premium project. The hardware is gorgeous, the integration is invisible, and the team treats my clients the way I would. That's rare in this industry.",
  },
];

export default function Testimonials() {
  const [idx, setIdx] = useState(0);
  const total = TESTIMONIALS.length;
  const timer = useRef(null);

  // Auto-advance every 7s, pause on hover/touch.
  const [paused, setPaused] = useState(false);
  useEffect(() => {
    if (paused) return;
    timer.current = setInterval(() => setIdx((i) => (i + 1) % total), 7000);
    return () => clearInterval(timer.current);
  }, [paused, total]);

  const go = (dir) => setIdx((i) => (i + dir + total) % total);

  return (
    <section
      data-testid="testimonials-section"
      className="relative border-t border-line bg-[var(--gx-black)] overflow-hidden"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="absolute inset-0 grain pointer-events-none opacity-60" />
      <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[640px] h-[640px] rounded-full bg-[radial-gradient(circle,rgba(201,169,97,0.10),transparent_60%)] pointer-events-none" />

      <div className="relative max-w-[1100px] mx-auto px-5 lg:px-10 py-20 lg:py-32">
        <div className="flex items-end justify-between gap-6 mb-12 lg:mb-16">
          <div>
            <p className="eyebrow mb-3">In their own words</p>
            <h2 className="h2-display max-w-[16ch]">
              Voices from <span className="text-gold">our homes.</span>
            </h2>
          </div>
          <div className="hidden md:flex gap-2 shrink-0">
            <button
              onClick={() => go(-1)}
              aria-label="Previous testimonial"
              data-testid="testimonial-prev"
              className="w-11 h-11 rounded-full border border-line hover:border-gold hover:text-gold text-dim flex items-center justify-center transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => go(1)}
              aria-label="Next testimonial"
              data-testid="testimonial-next"
              className="w-11 h-11 rounded-full border border-line hover:border-gold hover:text-gold text-dim flex items-center justify-center transition-colors"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="relative min-h-[280px] lg:min-h-[240px]">
          {TESTIMONIALS.map((t, i) => (
            <article
              key={t.name}
              data-testid={`testimonial-${i}`}
              className={`absolute inset-0 transition-all duration-700 ease-out ${
                i === idx
                  ? "opacity-100 translate-y-0 pointer-events-auto"
                  : "opacity-0 translate-y-3 pointer-events-none"
              }`}
              aria-hidden={i !== idx}
            >
              <Quote className="w-8 h-8 text-gold mb-6" strokeWidth={1.25} />
              <p className="font-display text-2xl sm:text-3xl lg:text-[34px] leading-snug tracking-[-0.01em] text-white max-w-[44ch]">
                "{t.quote}"
              </p>
              <div className="mt-8 flex items-center gap-4">
                <span className="w-12 h-12 rounded-full border border-gold/40 bg-gradient-to-br from-[#E2C07A]/20 to-[#8E7338]/20 flex items-center justify-center font-display text-gold text-lg">
                  {t.name.charAt(0)}
                </span>
                <div>
                  <p className="font-display text-base text-white">{t.name}</p>
                  <p className="text-[11px] text-mute tracking-[0.22em] uppercase mt-0.5">{t.role}</p>
                </div>
              </div>
            </article>
          ))}
        </div>

        {/* Indicator dots */}
        <div className="flex gap-2 mt-10">
          {TESTIMONIALS.map((_, i) => (
            <button
              key={i}
              onClick={() => setIdx(i)}
              aria-label={`Show testimonial ${i + 1}`}
              data-testid={`testimonial-dot-${i}`}
              className={`h-[2px] rounded-full transition-all duration-500 ${
                i === idx ? "w-10 bg-gold" : "w-5 bg-white/20 hover:bg-white/40"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
